import {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isValid } from 'date-fns';

// Use Cases
import {
  createTodayRange,
  createCurrentWeekRange,
  createCurrentMonthRange,
  createNext3MonthsRange,
  createCurrentQuarterRange,
  createRangeBySpan,
  navigatePeriod,
  createCurrentYearRange,
} from './useDateRangeUseCase';

/**
 * DateRange hook return type
 * @typedef {object} DateRangeHookReturn
 * @property {boolean} isOpenTimeRange
 * @property {(isOpenTimeRange: boolean) => void} setIsOpenTimeRange
 * @property {import('react-date-range').Range[]} ranges
 * @property {(ranges: import('react-date-range').Range[]) => void} setRanges
 * @property {import('react-date-range').RangeFocus} focus
 * @property {(focus: import('react-date-range').RangeFocus, type: ApiManagerDateRangeType) => void} handleSetFocus
 * @property {{ start: string, end: string }} timeRange
 * @property {(timeRange: { start: string, end: string }) => void} setTimeRange
 * @property {() => import('react-date-range').Range} handleTodayRange
 * @property {() => import('react-date-range').Range} handleCurrentMonthRange
 * @property {() => import('react-date-range').Range} handleNext3MonthsRange
 * @property {(dayPicked: import('react-date-range').Range[], span: ApiManagerDateRangeType) => void} handleSetRanges
 * @property {(newType: ApiManagerDateRangeType) => import('react-date-range').Range | null} handleTypeChange
 * @property {(direction: DateRangeDirection) => import('react-date-range').Range | null} handlePeriodArrows
 * @property {(range: import('react-date-range').Range, direction: DateRangeDirection, type: ApiManagerDateRangeType) => import('react-date-range').Range} navigatePeriod
 * @property {Array<{id: string, label: string, onClick: () => import('react-date-range').Range}>} dateRangePickerSidebarButtonsList
 * @property {Array<{value: ApiManagerDateRangeType, label: string}>} typeOptionsList
 * @property {boolean} calendarOpen
 * @property {(calendarOpen: boolean) => void} setCalendarOpen
 */

/**
 * Hook for managing date ranges with react-date-range
 * @param {object} props
 * @param {import('react-date-range').Range[]} props.initialRanges
 * @param {ApiManagerDateRangeType} props.type
 * @param {Array<ApiManagerDateRangeType>} props.typeOptions
 * @param {boolean} props.isLoading
 * @param {Date} [props.selectedDate] - Last clicked/anchored date from API
 * @returns {DateRangeHookReturn} Date range state and handlers
 */
const useDateRange = ({
  initialRanges, type, typeOptions, isLoading, selectedDate,
}) => {
  const { t } = useTranslation();
  const [isOpenTimeRange, setIsOpenTimeRange] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Need a local state to deal with the date range popover cancel and apply buttons
  const [ranges, setRanges] = useState(initialRanges);
  useEffect(() => {
    if (!isLoading) setRanges(initialRanges);
  }, [isLoading, initialRanges]);

  const [timeRange, setTimeRange] = useState({
    start: '00:00',
    end: '23:59',
  });

  /**
   * @type {[import('react-date-range').RangeFocus,
   * (focus: import('react-date-range').RangeFocus) => void]}
   */
  const [focus, setFocus] = useState(
    /** @type {import('react-date-range').RangeFocus} */ ([0, 0]),
  );

  /**
   * Handle today range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleTodayRange = useCallback(() => {
    const range = createTodayRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle current week range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleCurrentWeekRange = useCallback(() => {
    const range = createCurrentWeekRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle current month range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleCurrentMonthRange = useCallback(() => {
    const range = createCurrentMonthRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle next 3 months range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleNext3MonthsRange = useCallback(() => {
    const range = createNext3MonthsRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle current quarter range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleCurrentQuarterRange = useCallback(() => {
    const range = createCurrentQuarterRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle current year range
   * @returns {import('react-date-range').Range} The created range
   */
  const handleCurrentYearRange = useCallback(() => {
    const range = createCurrentYearRange();
    setRanges([range]);
    return range;
  }, []);

  /**
   * Handle set ranges from date picker
   * @param {import('react-date-range').Range[]} pickedValue
   * @param {ApiManagerDateRangeType} span
   */
  const handleSetRanges = (/** @type {import('react-date-range').Range[]} */ pickedValue, /** @type {ApiManagerDateRangeType} */ span) => {
    const dayPicked = pickedValue[0].startDate;

    if (span === 'custom') {
      const range = createRangeBySpan(
        pickedValue[0].startDate,
        span,
        pickedValue[0].endDate,
      );
      setRanges([range]);
    } else {
      const range = createRangeBySpan(dayPicked, span);
      setRanges([range]);
    }
  };

  const handleSetFocus = useCallback((
    /** @type {import('react-date-range').RangeFocus} */ focusArg,
    /** @type {ApiManagerDateRangeType} */ typeArg,
  ) => {
    if (typeArg === 'custom') {
      setFocus(focusArg);
    }
  }, [setFocus]);

  /**
   * Handle type change
   * @param {ApiManagerDateRangeType} newType - The new date range type
   * @returns {import('react-date-range').Range | null} The new range
   */
  const handleTypeChange = useCallback((/** @type {ApiManagerDateRangeType} */ newType) => {
    if (!ranges[0]) return null;
    if (newType === 'all') {
      return ranges[0];
    }
    const anchorDate = selectedDate ?? ranges[0]?.startDate;
    if (!anchorDate || !isValid(anchorDate)) return null;
    const newRange = createRangeBySpan(anchorDate, newType);
    setRanges([newRange]);
    return newRange;
  }, [ranges, selectedDate]);

  /**
   * Handle period navigation with arrows
   * @param {DateRangeDirection} direction - Navigation direction
   * @returns {import('react-date-range').Range | null} The new range
   */
  const handlePeriodArrows = useCallback((/** @type {DateRangeDirection} */ direction) => {
    if (!ranges[0]) return null;
    const newRange = navigatePeriod(ranges[0], direction, type);
    if (!newRange?.startDate) return null;
    setRanges([newRange]);
    return newRange;
  }, [ranges, navigatePeriod, type]);

  /**
   * Get the sidebar buttons list
   * @returns {Array<{id: string, label: string, onClick: () => void}>}
   */
  const dateRangePickerSidebarButtonsList = useMemo(() => {
    const today = {
      id: 'today',
      label: 'date.today',
      onClick: handleTodayRange,
    };
    const currentWeek = {
      id: 'current-week',
      label: 'date.currentWeek',
      onClick: handleCurrentWeekRange,
    };
    const currentMonth = {
      id: 'current-month',
      label: 'date.currentMonth',
      onClick: handleCurrentMonthRange,
    };
    const next3Months = {
      id: 'next-3-months',
      label: 'date.next3Months',
      onClick: handleNext3MonthsRange,
    };
    const currentQuarter = {
      id: 'current-quarter',
      label: 'date.currentQuarter',
      onClick: handleCurrentQuarterRange,
    };
    const currentYear = {
      id: 'current-year',
      label: 'date.currentYear',
      onClick: handleCurrentYearRange,
    };
    switch (type) {
      case 'day':
        return [{
          ...today,
        }];
      case 'week':
        return [{ ...currentWeek }];
      case 'month':
        return [{ ...currentMonth }];
      case 'quarter':
        return [{ ...currentQuarter }];
      case 'year':
        return [{ ...currentYear }];
      case 'custom': {
        return [{ ...today }, { ...currentMonth }, { ...next3Months }];
      }
      default:
        return [];
    }
  }, [type]);

  /**
   * Get the type options list
   * @returns {Array<{value: ApiManagerDateRangeType, label: string}>}
   */
  const typeOptionsList = useMemo(() => {
    if (isLoading || !typeOptions) return [];
    return typeOptions.map((typeOption) => ({
      value: typeOption,
      label: t(`date.${typeOption}`),
    }));
  }, [typeOptions]);

  return {
    ranges,
    setRanges,
    focus,
    handleSetFocus,
    timeRange,
    setTimeRange,
    handleTodayRange,
    isOpenTimeRange,
    setIsOpenTimeRange,
    handleCurrentMonthRange,
    handleNext3MonthsRange,
    handleSetRanges,
    handleTypeChange,
    handlePeriodArrows,
    navigatePeriod,
    dateRangePickerSidebarButtonsList,
    typeOptionsList,
    calendarOpen,
    setCalendarOpen,
  };
};

export default useDateRange;
